import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/ui/Layout';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { clientService } from '../../services/clientService';

import SubHeader from '../../components/ui/SubHeader';
import ActionBoxes from '../../components/ui/ActionBoxes';
import ClientCategoryList from './components/ClientCategoryList';
import ClientGridView from './components/ClientGridView';
import ClientDetailsPanel from './components/ClientDetailsPanel';
import BulkActionToolbar from './components/BulkActionToolbar';
import MessageTemplateModal from './components/MessageTemplateModal';
import FollowUpScheduler from './components/FollowUpScheduler';
import ClientFilterModal from './components/ClientFilterModal';
import ActiveClientsModal from './components/ActiveClientsModal';
import PendingFollowUpModal from './components/PendingFollowUpModal';
import HighPriorityModal from './components/HighPriorityModal';
import BuyerPreferencesModal from './components/BuyerPreferencesModal';
import RenterPreferencesModal from './components/RenterPreferencesModal';
import InvestorCriteriaModal from './components/InvestorCriteriaModal';
import VIPManagementModal from './components/VIPManagementModal';
import AddClientModal from './components/AddClientModal';
import ExportClientsModal from './components/ExportClientsModal';
import realtimeNotificationService from '../../services/realtimeNotificationService';

const ClientRelationshipManagement = () => {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
  const [currentScheduleClient, setCurrentScheduleClient] = useState(null);
  const [isQuickMenuOpen, setIsQuickMenuOpen] = useState(false);

  // New modal states
  const [isClientFilterModalOpen, setIsClientFilterModalOpen] = useState(false);
  const [isActiveClientsModalOpen, setIsActiveClientsModalOpen] = useState(false);
  const [isPendingFollowUpModalOpen, setIsPendingFollowUpModalOpen] = useState(false);
  const [isHighPriorityModalOpen, setIsHighPriorityModalOpen] = useState(false);
  const [isBuyerPreferencesModalOpen, setIsBuyerPreferencesModalOpen] = useState(false);
  const [isRenterPreferencesModalOpen, setIsRenterPreferencesModalOpen] = useState(false);
  const [isInvestorCriteriaModalOpen, setIsInvestorCriteriaModalOpen] = useState(false);
  const [isVIPManagementModalOpen, setIsVIPManagementModalOpen] = useState(false);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isExportClientsModalOpen, setIsExportClientsModalOpen] = useState(false);

  // Real-time notification states
  const [realtimeNotification, setRealtimeNotification] = useState(null);
  const [showRealtimeToast, setShowRealtimeToast] = useState(false);

  // NEW: States for real data
  const [clients, setClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const [clientsError, setClientsError] = useState(null);

  // Client management action boxes
  const clientActions = [
  {
    id: 'all-clients',
    icon: 'Users',
    label: 'جميع العملاء',
    color: '#3B82F6',
    bgGradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(96, 165, 250, 0.25) 100%)',
    borderColor: 'rgba(59, 130, 246, 0.5)',
    onClick: () => setIsClientFilterModalOpen(true)
  },
  {
    id: 'active',
    icon: 'UserCheck',
    label: 'عملاء نشطون',
    color: '#10B981',
    bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(52, 211, 153, 0.25) 100%)',
    borderColor: 'rgba(16, 185, 129, 0.5)',
    onClick: () => setIsActiveClientsModalOpen(true)
  },
  {
    id: 'pending',
    icon: 'Clock',
    label: 'قيد المتابعة',
    color: '#F59E0B',
    bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(251, 191, 36, 0.25) 100%)',
    borderColor: 'rgba(245, 158, 11, 0.5)',
    onClick: () => setIsPendingFollowUpModalOpen(true)
  },
  {
    id: 'high-priority',
    icon: 'AlertCircle',
    label: 'أولوية عالية',
    color: '#EF4444',
    bgGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(248, 113, 113, 0.25) 100%)',
    borderColor: 'rgba(239, 68, 68, 0.5)',
    onClick: () => setIsHighPriorityModalOpen(true)
  },
  {
    id: 'buyers',
    icon: 'ShoppingCart',
    label: 'مشترون',
    color: '#8B5CF6',
    bgGradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(167, 139, 250, 0.25) 100%)',
    borderColor: 'rgba(139, 92, 246, 0.5)',
    onClick: () => setIsBuyerPreferencesModalOpen(true)
  },
  {
    id: 'renters',
    icon: 'Home',
    label: 'مستأجرون',
    color: '#06B6D4',
    bgGradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(34, 211, 238, 0.25) 100%)',
    borderColor: 'rgba(6, 182, 212, 0.5)',
    onClick: () => setIsRenterPreferencesModalOpen(true)
  },
  {
    id: 'investors',
    icon: 'TrendingUp',
    label: 'مستثمرون',
    color: '#EC4899',
    bgGradient: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(244, 114, 182, 0.25) 100%)',
    borderColor: 'rgba(236, 72, 153, 0.5)',
    onClick: () => setIsInvestorCriteriaModalOpen(true)
  },
  {
    id: 'vip',
    icon: 'Star',
    label: 'عملاء VIP',
    color: '#F59E0B',
    bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(251, 191, 36, 0.25) 100%)',
    borderColor: 'rgba(245, 158, 11, 0.5)',
    onClick: () => setIsVIPManagementModalOpen(true)
  },
  {
    id: 'add-client',
    icon: 'UserPlus',
    label: 'إضافة عميل',
    color: '#14B8A6',
    bgGradient: 'linear-gradient(135deg, rgba(20, 184, 166, 0.2) 0%, rgba(45, 212, 191, 0.25) 100%)',
    borderColor: 'rgba(20, 184, 166, 0.5)',
    onClick: () => setIsAddClientModalOpen(true)
  },
  {
    id: 'bulk-message',
    icon: 'Send',
    label: 'رسالة جماعية',
    color: '#A855F7',
    bgGradient: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(192, 132, 252, 0.25) 100%)',
    borderColor: 'rgba(168, 85, 247, 0.5)',
    onClick: () => setIsMessageModalOpen(true)
  },
  {
    id: 'export',
    icon: 'Download',
    label: 'تصدير',
    color: '#64748B',
    bgGradient: 'linear-gradient(135deg, rgba(100, 116, 139, 0.2) 0%, rgba(148, 163, 184, 0.25) 100%)',
    borderColor: 'rgba(100, 116, 139, 0.5)',
    onClick: () => setIsExportClientsModalOpen(true)
  }];


  const categories = React.useMemo(() => {
    const all = clients?.length || 0;
    const active = clients?.filter(c => c?.status === 'active')?.length || 0;
    const pending = clients?.filter(c => c?.status === 'pending')?.length || 0;
    const highPriority = clients?.filter(c => c?.priority === 'high')?.length || 0;
    const buyers = clients?.filter(c => c?.category === 'مشتري')?.length || 0;
    const renters = clients?.filter(c => c?.category === 'مستأجر')?.length || 0;
    const investors = clients?.filter(c => c?.category === 'مستثمر')?.length || 0;
    const vip = clients?.filter(c => c?.category === 'VIP')?.length || 0;

    return [
      { id: 'all', name: 'جميع العملاء', icon: 'Users', count: all },
      { id: 'active', name: 'عملاء نشطون', icon: 'UserCheck', count: active },
      { id: 'pending', name: 'قيد المتابعة', icon: 'Clock', count: pending },
      { id: 'high_priority', name: 'أولوية عالية', icon: 'AlertCircle', count: highPriority },
      { id: 'buyers', name: 'مشترون', icon: 'ShoppingCart', count: buyers },
      { id: 'renters', name: 'مستأجرون', icon: 'Home', count: renters },
      { id: 'investors', name: 'مستثمرون', icon: 'TrendingUp', count: investors },
      { id: 'vip', name: 'عملاء VIP', icon: 'Star', count: vip }
    ];
  }, [clients]);


  const breadcrumbItems = [
  { label: 'الرئيسية', path: '/property-management-dashboard', icon: 'Home' },
  { label: 'إدارة علاقات العملاء', path: '/client-relationship-management', icon: 'Users' }];


  const quickActions = [
  {
    label: 'إضافة عميل جديد',
    icon: 'UserPlus',
    onClick: () => console.log('Add new client'),
    roles: ['admin', 'property_manager', 'client_manager'],
    variant: 'primary'
  },
  {
    label: 'استيراد عملاء',
    icon: 'Upload',
    onClick: () => console.log('Import clients'),
    roles: ['admin', 'client_manager'],
    variant: 'default'
  },
  {
    label: 'تصدير قائمة العملاء',
    icon: 'Download',
    onClick: () => handleExportClients(),
    roles: ['admin', 'client_manager'],
    variant: 'default'
  }];


  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedClient(null);
  };

  const handleClientSelect = (clientId) => {
    const client = clients?.find((c) => c?.id === clientId);
    setSelectedClient(client);
  };

  const handleBulkSelect = (clientIds) => {
    setSelectedClients(clientIds);
  };

  const handleSendBulkMessage = () => {
    setIsMessageModalOpen(true);
  };

  const handleExportClients = () => {
    console.log('Exporting clients:', selectedClients?.length > 0 ? selectedClients : 'all');
    alert(`تم تصدير ${selectedClients?.length > 0 ? selectedClients?.length : clients?.length} عميل بنجاح`);
  };

  const handleClearSelection = () => {
    setSelectedClients([]);
  };

  const handleSendMessage = (clientId) => {
    setSelectedClients([clientId]);
    setIsMessageModalOpen(true);
  };

  const handleScheduleFollowUp = (clientId) => {
    const client = clients?.find((c) => c?.id === clientId);
    setCurrentScheduleClient(client);
    setIsSchedulerOpen(true);
  };

  const handleGenerateForm = (clientId) => {
    console.log('Generating form for client:', clientId);
    alert('سيتم فتح صفحة إنشاء النماذج');
  };

  const handleMessageSend = (data) => {
    console.log('Sending message:', data);
    alert(`تم إرسال الرسالة إلى ${data?.recipientCount} عميل بنجاح`);
    setSelectedClients([]);
  };

  const handleFollowUpSchedule = (data) => {
    console.log('Scheduling follow-up:', data);
    alert(`تم جدولة المتابعة مع ${data?.clientName} بنجاح`);
  };

  const handleQuickMenuToggle = () => {
    setIsQuickMenuOpen(!isQuickMenuOpen);
  };

  const filteredClients = React.useMemo(() => {
    return clients?.filter((client) => {
      // Category filter
      if (selectedCategory !== 'all') {
        if (selectedCategory === 'active' && client?.status !== 'active') return false;
        if (selectedCategory === 'pending' && client?.status !== 'pending') return false;
        if (selectedCategory === 'high_priority' && client?.priority !== 'high') return false;
        if (selectedCategory === 'buyers' && client?.category !== 'مشتري') return false;
        if (selectedCategory === 'renters' && client?.category !== 'مستأجر') return false;
        if (selectedCategory === 'investors' && client?.category !== 'مستثمر') return false;
        if (selectedCategory === 'vip' && client?.category !== 'VIP') return false;
      }

      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery?.toLowerCase();
        const searchFields = [
          client?.name,
          client?.email,
          client?.phone,
          client?.preferred_location
        ];
        
        const matches = searchFields?.some(field => 
          field?.toLowerCase()?.includes(searchLower)
        );
        
        if (!matches) return false;
      }

      return true;
    });
  }, [clients, selectedCategory, searchQuery]);


  const handleApplyFilters = (filters) => {
    console.log('Applying filters:', filters);
    // Implement filter logic here
  };

  const handleSetPriority = async (priorityData) => {
    try {
      // Update client priority in database
      await clientService?.update(priorityData?.clientId, { priority: priorityData?.priority }, user?.id);
      
      // Reload clients
      await loadClients();
      
      alert('تم تحديث الأولوية بنجاح');
    } catch (error) {
      console.error('Error setting priority:', error);
      alert('فشل في تحديث الأولوية: ' + error?.message);
    }
  };

  const handleSavePreferences = async (preferences) => {
    try {
      // Update client preferences in database
      const updateData = {
        preferred_location: preferences?.preferredLocation,
        budget_min: preferences?.budgetMin ? parseFloat(preferences?.budgetMin) : null,
        budget_max: preferences?.budgetMax ? parseFloat(preferences?.budgetMax) : null,
        property_type: preferences?.propertyType,
        bedrooms: preferences?.bedrooms ? parseInt(preferences?.bedrooms) : null
      };
      
      await clientService?.update(preferences?.clientId, updateData, user?.id);
      
      // Reload clients
      await loadClients();
      
      alert('تم حفظ التفضيلات بنجاح');
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('فشل في حفظ التفضيلات: ' + error?.message);
    }
  };

  const handleSaveCriteria = async (criteria) => {
    try {
      // Update investor criteria in database
      const updateData = {
        preferred_location: criteria?.location,
        budget_min: criteria?.minBudget ? parseFloat(criteria?.minBudget) : null,
        budget_max: criteria?.maxBudget ? parseFloat(criteria?.maxBudget) : null,
        property_type: criteria?.propertyType,
        notes: criteria?.investmentGoals
      };
      
      await clientService?.update(criteria?.clientId, updateData, user?.id);
      
      // Reload clients
      await loadClients();
      
      alert('تم حفظ معايير الاستثمار بنجاح');
    } catch (error) {
      console.error('Error saving criteria:', error);
      alert('فشل في حفظ المعايير: ' + error?.message);
    }
  };

  const handleSaveVIPData = async (vipData) => {
    try {
      // Update VIP client data in database
      const updateData = {
        category: 'VIP',
        priority: 'high',
        notes: vipData?.specialRequirements
      };
      
      await clientService?.update(vipData?.clientId, updateData, user?.id);
      
      // Reload clients
      await loadClients();
      
      alert('تم حفظ بيانات VIP بنجاح');
    } catch (error) {
      console.error('Error saving VIP data:', error);
      alert('فشل في حفظ بيانات VIP: ' + error?.message);
    }
  };

  const handleAddClient = (clientData) => {
    console.log('Adding client:', clientData);
    // Implement client addition logic here
  };

  const handleExport = (exportOptions) => {
    console.log('Exporting with options:', exportOptions);
    // Implement export logic here
  };

  // Setup real-time subscriptions for clients
  useEffect(() => {
    // Subscribe to client changes
    const unsubscribeClients = realtimeNotificationService?.subscribeToClients((notification) => {
      console.log('Real-time client update:', notification);
      
      // Show toast notification
      const message = notification?.event === 'INSERT' ?'تم إضافة عميل جديد: ' + (notification?.data?.name || 'عميل')
        : notification?.event === 'UPDATE' ?'تم تحديث بيانات عميل: ' + (notification?.data?.name || 'عميل')
        : 'تم حذف عميل';
      
      setRealtimeNotification({
        message,
        type: 'client',
        data: notification?.data,
        priority: notification?.data?.priority || 'medium',
        category: notification?.data?.category
      });
      setShowRealtimeToast(true);
      
      // Auto-hide toast after 5 seconds
      setTimeout(() => setShowRealtimeToast(false), 5000);
    });

    // Subscribe to client recommendations
    const unsubscribeRecommendations = realtimeNotificationService?.subscribeToRecommendations((notification) => {
      console.log('Real-time recommendation update for client:', notification);
      
      if (notification?.event === 'INSERT' || notification?.event === 'UPDATE') {
        const message = notification?.event === 'INSERT' ?'توصية عقارية جديدة لعميل' :'تم تحديث توصية عقارية';
        
        setRealtimeNotification({
          message,
          type: 'recommendation',
          data: notification?.data,
          priority: notification?.data?.priority || 'medium'
        });
        setShowRealtimeToast(true);
        setTimeout(() => setShowRealtimeToast(false), 5000);
      }
    });

    // Subscribe to contract changes (for client contracts)
    const unsubscribeContracts = realtimeNotificationService?.subscribeToContracts((notification) => {
      console.log('Real-time contract update for client:', notification);
      
      if (notification?.event === 'INSERT') {
        const message = 'تم إنشاء عقد جديد لعميل';
        
        setRealtimeNotification({
          message,
          type: 'client_contract',
          data: notification?.data,
          priority: 'medium'
        });
        setShowRealtimeToast(true);
        setTimeout(() => setShowRealtimeToast(false), 5000);
      }
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeClients();
      unsubscribeRecommendations();
      unsubscribeContracts();
    };
  }, []);

  // NEW: Load clients from Supabase
  useEffect(() => {
    if (user?.id) {
      loadClients();
    }
  }, [user?.id]);

  const loadClients = async () => {
    try {
      setIsLoadingClients(true);
      setClientsError(null);
      
      const data = await clientService?.getAll(user?.id);
      setClients(data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClientsError(error?.message || 'فشل في تحميل العملاء');
    } finally {
      setIsLoadingClients(false);
    }
  };

  return (
    <Layout userRole="client_manager" userName="سارة أحمد" onQuickMenuToggle={handleQuickMenuToggle}>
      <SubHeader pageTitle="إدارة العملاء" />
      <ActionBoxes
        actions={clientActions}
        showNewContractModal={false}
        setShowNewContractModal={() => {}}
        contractTypeConfig={{}} />
      <div className="space-y-4 md:space-y-6">

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6" style={{ height: 'calc(100vh - 240px)' }}>
          <div className="lg:col-span-2">
            <ClientCategoryList
              categories={categories}
              selectedCategory={selectedCategory}
              onCategorySelect={handleCategorySelect}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery} />

          </div>

          <div className="lg:col-span-7">
            {isLoadingClients ? (
              <div className="flex flex-col items-center justify-center h-full py-12">
                <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-4"></div>
                <p className="text-sm text-muted-foreground">جاري تحميل العملاء...</p>
              </div>
            ) : clientsError ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <Icon name="AlertTriangle" size={48} color="var(--color-error)" className="mb-4" />
                <p className="text-sm text-error mb-4">{clientsError}</p>
                <button
                  onClick={loadClients}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  إعادة المحاولة
                </button>
              </div>
            ) : filteredClients?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mb-4" />
                <p className="text-sm text-muted-foreground">
                  لا توجد عملاء تطابق معايير البحث
                </p>
              </div>
            ) : (
              <ClientGridView
                clients={filteredClients}
                selectedClient={selectedClient?.id}
                onSelectClient={handleClientSelect}
                onClientSelect={handleClientSelect}
                onBulkSelect={handleBulkSelect}
                selectedClients={selectedClients}
              />
            )}
          </div>

          <div className="lg:col-span-3">
            <ClientDetailsPanel
              client={selectedClient}
              onClose={() => setSelectedClient(null)}
              onSendMessage={handleSendMessage}
              onScheduleFollowUp={handleScheduleFollowUp}
              onGenerateForm={handleGenerateForm} />

          </div>
        </div>
      </div>
      <BulkActionToolbar
        selectedCount={selectedClients?.length}
        onSendBulkMessage={handleSendBulkMessage}
        onExportClients={handleExportClients}
        onClearSelection={handleClearSelection} />
      {/* All Modal Components */}
      <ClientFilterModal
        isOpen={isClientFilterModalOpen}
        onClose={() => setIsClientFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters} />
      <ActiveClientsModal
        isOpen={isActiveClientsModalOpen}
        onClose={() => setIsActiveClientsModalOpen(false)}
        onApplyFilters={handleApplyFilters} />
      <PendingFollowUpModal
        isOpen={isPendingFollowUpModalOpen}
        onClose={() => setIsPendingFollowUpModalOpen(false)}
        onScheduleFollowUp={handleScheduleFollowUp} />
      <HighPriorityModal
        isOpen={isHighPriorityModalOpen}
        onClose={() => setIsHighPriorityModalOpen(false)}
        onSetPriority={handleSetPriority} />
      <BuyerPreferencesModal
        isOpen={isBuyerPreferencesModalOpen}
        onClose={() => setIsBuyerPreferencesModalOpen(false)}
        onSavePreferences={handleSavePreferences} />
      <RenterPreferencesModal
        isOpen={isRenterPreferencesModalOpen}
        onClose={() => setIsRenterPreferencesModalOpen(false)}
        onSavePreferences={handleSavePreferences} />
      <InvestorCriteriaModal
        isOpen={isInvestorCriteriaModalOpen}
        onClose={() => setIsInvestorCriteriaModalOpen(false)}
        onSaveCriteria={handleSaveCriteria} />
      <VIPManagementModal
        isOpen={isVIPManagementModalOpen}
        onClose={() => setIsVIPManagementModalOpen(false)}
        onSaveVIPData={handleSaveVIPData} />
      <AddClientModal
        isOpen={isAddClientModalOpen}
        onClose={() => setIsAddClientModalOpen(false)}
        onAddClient={handleAddClient} />
      <ExportClientsModal
        isOpen={isExportClientsModalOpen}
        onClose={() => setIsExportClientsModalOpen(false)}
        onExport={handleExport} />
      <MessageTemplateModal
        isOpen={isMessageModalOpen}
        onClose={() => {
          setIsMessageModalOpen(false);
          if (selectedClients?.length === 1) setSelectedClients([]);
        }}
        onSend={handleMessageSend}
        recipientCount={selectedClients?.length} />
      <FollowUpScheduler
        isOpen={isSchedulerOpen}
        onClose={() => {
          setIsSchedulerOpen(false);
          setCurrentScheduleClient(null);
        }}
        onSchedule={handleFollowUpSchedule}
        clientName={currentScheduleClient?.name} />
      {/* Real-time notification toast */}
      {showRealtimeToast && realtimeNotification && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
          <div className={`bg-white rounded-lg shadow-2xl border-r-4 p-4 min-w-[300px] max-w-md ${
            realtimeNotification?.priority === 'high' ?'border-red-500' 
              : realtimeNotification?.category === 'VIP' ?'border-purple-500' :'border-green-500'
          }`}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  realtimeNotification?.priority === 'high' ?'bg-red-100'
                    : realtimeNotification?.category === 'VIP' ?'bg-purple-100' :'bg-green-100'
                }`}>
                  <Icon 
                    name="Users" 
                    className={`w-5 h-5 ${
                      realtimeNotification?.priority === 'high' ?'text-red-600'
                        : realtimeNotification?.category === 'VIP' ?'text-purple-600' :'text-green-600'
                    }`} 
                  />
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">
                  تحديث فوري
                </h4>
                <p className="text-sm text-gray-700">
                  {realtimeNotification?.message}
                </p>
                {realtimeNotification?.category && (
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                    realtimeNotification?.category === 'VIP' ?'bg-purple-100 text-purple-700'
                      : realtimeNotification?.category === 'مشتري' ?'bg-blue-100 text-blue-700'
                      : realtimeNotification?.category === 'مستأجر' ?'bg-green-100 text-green-700' :'bg-gray-100 text-gray-700'
                  }`}>
                    {realtimeNotification?.category}
                  </span>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {new Date()?.toLocaleTimeString('ar-JO', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <button
                onClick={() => setShowRealtimeToast(false)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <Icon name="X" className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );

};

export default ClientRelationshipManagement;